/**
 * Stale-while-revalidate cache for the Landing payload.
 *
 * Why: cold-start Landing fired ~6 parallel API calls before rendering anything
 * past the header — on a flaky Jio handoff the menu area stayed blank for
 * seconds while the network resolved. Cache hits let the page paint last-seen
 * content synchronously on mount; the network call still fires in the
 * background and swaps in fresh data when it lands.
 *
 * Storage: localStorage, keyed per-branch for menu items, global for the rest.
 * Schema-versioned so future shape changes invalidate old rows. 24h TTL so a
 * customer who returns the next morning still sees instant content but can't
 * be stuck on a week-old menu after a Rista sync changes the catalogue.
 *
 * Safety: the cache is only a paint hint. Stale pricing / availability can't
 * cause wrong charges — backend re-validates at order creation (Business Rule
 * #27 server hard-stop), and Cart.js / Checkout.js refetch on tab refocus.
 *
 * Every helper here is silent: QuotaExceededError, private-browsing stub,
 * corrupt JSON, and SSR (no window) all degrade to "no cache" rather than
 * throwing.
 */

const TTL_MS = 24 * 60 * 60 * 1000;
const SCHEMA_VERSION = 1;

export const CACHE_KEYS = {
  branches: 'pbc_cache_branches',
  menu: (branchId) => `pbc_cache_menu_${branchId}`,
  categoryImages: 'pbc_cache_category_images',
  comboOffers: 'pbc_cache_combo_offers',
  subscriptions: 'pbc_cache_subscriptions',
  // Per-branch Today's-Specials list for OfferOfTheDay — lets that page paint
  // last-seen offers on a tab re-visit instead of flashing its skeleton.
  offerOfDay: (branchId) => `pbc_cache_offer_of_day_${branchId}`,
  // Per-user recent orders (Landing "Order Again") + per-zone/branch promo banners.
  // Both pop in async on Home and shift layout on a tab re-visit; caching paints
  // them with the first frame. Keyed by user so a different login can't see them.
  recentOrders: (userId) => `pbc_cache_recent_orders_${userId}`,
  banners: (zone, branchId) => `pbc_cache_banners_${zone}_${branchId || 'none'}`,
  // Per-user/branch free-coffee progress — the Offers "Coffee Rewards" section is
  // gated on it, so without a cached value the section pops in (shifts layout) a
  // beat after the page paints on a re-visit. Paint hint only; refetch corrects.
  freeCoffee: (userId, branchId) => `pbc_cache_free_coffee_${userId}_${branchId || 'none'}`,
  // The admin-arranged menu layout (sections → category order, home quick links),
  // global for every store — read by hooks/useMenuLayout.js for an instant first paint.
  menuLayout: 'pbc_cache_menu_layout',
};

/**
 * Read a cache entry. Returns null on miss / expired / corrupt / unavailable
 * storage. Used by: Landing.js lazy useState initializers + fetcher fallbacks
 */
export function readCache(key) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== SCHEMA_VERSION) return null;
    if (typeof parsed?.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Write a cache entry. Silent on QuotaExceededError / stringify failure /
 * unavailable storage. Used by: Landing.js fetchers after a successful response
 */
export function writeCache(key, data) {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (data === undefined || data === null) return;
    const payload = JSON.stringify({ v: SCHEMA_VERSION, ts: Date.now(), data });
    window.localStorage.setItem(key, payload);
  } catch {
    // QuotaExceededError, SecurityError (private browsing), JSON cyclic refs.
    // All non-fatal: the previous cache entry stays valid; next paint just
    // falls through to the network call.
  }
}
